import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Link2, Sparkles, ChevronDown, ChevronUp, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ShortenerForm({ onCreated }) {
    const { user } = useAuth();
    const [url, setUrl] = useState("");
    const [alias, setAlias] = useState("");
    const [password, setPassword] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [aliasCheck, setAliasCheck] = useState({ status: "idle" }); // idle | checking | ok | taken

    // Debounced alias availability check
    useEffect(() => {
        const value = alias.trim();
        if (!value) { setAliasCheck({ status: "idle" }); return; }
        setAliasCheck({ status: "checking" });
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.get(`/links/check-alias/${encodeURIComponent(value)}`);
                if (data.available) {
                    setAliasCheck({ status: "ok" });
                } else {
                    setAliasCheck({ status: "taken", reason: data.reason, level: data.level });
                }
            } catch {
                setAliasCheck({ status: "idle" });
            }
        }, 400);
        return () => clearTimeout(handle);
    }, [alias]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!url.trim()) return;
        if (alias.trim() && aliasCheck.status === "taken") {
            setError(aliasCheck.reason || "Alias is not available");
            return;
        }
        setLoading(true);
        try {
            const endpoint = user ? "/links" : "/preview";
            const { data } = await api.post(endpoint, {
                long_url: url.trim(),
                alias: alias.trim() || undefined,
                password: password.trim() || undefined,
            });
            if (data?.existing) {
                toast.info("You've already shortened this URL — here's the existing link and QR code.", { duration: 4000 });
            }
            onCreated?.(data);
            setUrl(""); setAlias(""); setPassword("");
            setAliasCheck({ status: "idle" });
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    const aliasHintTone = aliasCheck.status === "ok" ? "text-[hsl(100,76%,35%)] dark:text-[hsl(100,70%,55%)]"
        : aliasCheck.status === "taken" ? "text-destructive"
        : "text-muted-foreground";

    return (
        <form onSubmit={submit} className="w-full" data-testid="shortener-form">
            <div className="tracing-beam group flex flex-col gap-2 rounded-2xl border-2 border-border bg-card p-2 transition-shadow sm:flex-row sm:items-center sm:p-3">
                <div className="flex flex-1 items-center gap-3 px-3">
                    <Link2 className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                    <Input
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="Paste your long URL here..."
                        className="h-12 border-0 bg-transparent text-base shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        data-testid="shorten-input"
                        required
                    />
                </div>
                <Button
                    type="submit"
                    disabled={loading || !url.trim() || (alias.trim() && aliasCheck.status === "taken")}
                    className="h-12 gap-2 bg-primary px-6 text-base font-semibold text-primary-foreground transition-[background,transform] hover:bg-primary/90 hover:-translate-y-0.5"
                    data-testid="shorten-submit-btn"
                >
                    <Sparkles className="h-4 w-4" />
                    {loading ? "Shortening..." : "Shorten"}
                </Button>
            </div>

            <button
                type="button"
                onClick={() => setShowAdvanced((s) => !s)}
                className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="advanced-toggle-btn"
            >
                {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Advanced options
            </button>

            {showAdvanced && (
                <div className="mt-3 grid grid-cols-1 gap-4 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
                    <div>
                        <Label htmlFor="alias-input" className="text-xs uppercase tracking-wider text-muted-foreground">
                            Custom alias
                        </Label>
                        <Input
                            id="alias-input"
                            value={alias}
                            onChange={(e) => setAlias(e.target.value)}
                            placeholder="e.g. my-launch"
                            className="mt-2 font-mono"
                            data-testid="alias-input"
                        />
                        {alias.trim() && (
                            <div className={`mt-2 flex items-center gap-1.5 text-xs ${aliasHintTone}`} data-testid="alias-status">
                                {aliasCheck.status === "checking" && <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking availability…</>}
                                {aliasCheck.status === "ok" && <><Check className="h-3.5 w-3.5" /> <span className="font-mono">/{alias.trim()}</span> is available</>}
                                {aliasCheck.status === "taken" && <><X className="h-3.5 w-3.5" /> {aliasCheck.reason || "Not available"}</>}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label htmlFor="password-input" className="text-xs uppercase tracking-wider text-muted-foreground">
                            Password (optional)
                        </Label>
                        <Input
                            id="password-input"
                            type="text"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Protect this link"
                            className="mt-2"
                            data-testid="link-password-input"
                        />
                    </div>
                </div>
            )}

            {error && (
                <div className="mt-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="shorten-error">
                    {error}
                </div>
            )}
        </form>
    );
}
